import { BusinessPurpose } from '@ebalance/proto/bfftypes/v1/business_purpose_pb'
import { LicenseType } from '@ebalance/proto/bfftypes/v1/license_type_pb'

import { type BusinessMasterRow } from '~/hooks/businessMaster/useListBusinessMasters'
import type { CivilDate } from '~/models/civilDate'
import { isActiveOn } from '~/models/validPeriod'

/**
 * BG区分として選択しうる LicenseType (送配電 TRANSMISSION / UNSPECIFIED を除く 4 値)。
 * form の licenseType field と同じ狭い union で、 絞り込み結果を setValue する際の
 * 型整合に使う。
 */
export type BgLicenseType =
  | LicenseType.RETAIL
  | LicenseType.GENERATION
  | LicenseType.SELF_WHEELING
  | LicenseType.DR

/**
 * BG区分 select の全選択肢 (表示順)。
 *
 * spec `docs/specs/admin/master/occto-master/bg.md` L23 (「小売 / 発電 / 自己託送 /
 * DR の 4 択」) 準拠の並びに統一する (Decision 1、
 * `bg-license-filter-by-business-master` 設計書)。 BG区分 select は
 * `LicenseType.TRANSMISSION` を選択肢に含まない (BG は送配電区分を持たない)。
 */
export const LICENSE_TYPE_OPTIONS: BgLicenseType[] = [
  LicenseType.RETAIL,
  LicenseType.GENERATION,
  LicenseType.SELF_WHEELING,
  LicenseType.DR,
]

/**
 * AC-OMB-023/024: BG区分 select の選択肢を、 同一 organization 内の事業者マスタ
 * (`businessMasters`、 既に organization スコープ) の targetDate 時点の登録状況で
 * 絞り込む。
 *
 * 判定軸:
 * - RETAIL / GENERATION / DR: 事業者マスタに一致する LicenseCd (`licenseTypes`) の
 *   事業者が 1 件以上あれば選択可
 * - SELF_WHEELING: 事業者マスタに Purpose=自己託送 (`purpose`) の事業者が 1 件以上
 *   あれば選択可 (自己託送は LicenseCd 軸に存在せず Purpose 軸で表現される別区分)
 *
 * 呼び出し側 (Create モーダルの select 描画 / BG タブの新規登録ボタン非活性判定)
 * は本関数の結果を再利用する。
 */
export function availableLicenseTypes(
  businessMasters: BusinessMasterRow[],
  targetDate: CivilDate,
): BgLicenseType[] {
  const activeBusinessMasters = businessMasters.filter((businessMaster) =>
    isActiveOn(businessMaster.validFrom, businessMaster.validTo, targetDate),
  )

  return LICENSE_TYPE_OPTIONS.filter((licenseType) =>
    licenseType === LicenseType.SELF_WHEELING
      ? activeBusinessMasters.some(
          (bm) => bm.purpose === BusinessPurpose.SELF_WHEELING,
        )
      : activeBusinessMasters.some((bm) =>
          bm.licenseTypes.includes(licenseType),
        ),
  )
}
